import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useGenerateExtensionTokenMutation,
  useLazyGetExtensionTokenQuery,
  useRevokeExtensionTokenMutation,
} from "../../../../../app/services/book/saved_tours"
import { formatDate } from "../../../admin_extension_tokens/utils/date"

const ExtensionTab = ({ active }) => {
  const { t } = useTranslation()

  const [fetchStatus, { data: statusData, isFetching: statusLoading }] =
    useLazyGetExtensionTokenQuery()
  const [generateToken, { isLoading: generating }] = useGenerateExtensionTokenMutation()
  const [revokeToken, { isLoading: revoking }] = useRevokeExtensionTokenMutation()

  const [connectionString, setConnectionString] = useState(null)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState(null)
  const [confirmRevokeId, setConfirmRevokeId] = useState(null)

  useEffect(() => {
    if (active) {
      fetchStatus()
      setConnectionString(null)
      setCopied(false)
      setError(null)
      setConfirmRevokeId(null)
    }
  }, [active, fetchStatus])

  const tokens = statusData?.tokens || []
  const maxTokens = statusData?.max_tokens || 5
  const connected = tokens.length > 0
  const limitReached = tokens.length >= maxTokens

  const handleGenerate = async () => {
    setError(null)
    try {
      const res = await generateToken().unwrap()
      setConnectionString(res.connection_string)
      setCopied(false)
      fetchStatus()
    } catch (e) {
      setError(
        e?.data?.message === "tokens_limit_reached"
          ? t("saved_tours.extension.limit_reached")
          : t("saved_tours.extension.generate_error")
      )
    }
  }

  const handleRevoke = async (id) => {
    setError(null)
    try {
      await revokeToken(id).unwrap()
      setConfirmRevokeId(null)
      fetchStatus()
    } catch (e) {
      setError(t("saved_tours.extension.revoke_error"))
    }
  }

  const handleCopy = async () => {
    if (!connectionString) return
    try {
      await navigator.clipboard.writeText(connectionString)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setError(t("saved_tours.extension.copy_error"))
    }
  }

  return (
    <div>
      {statusLoading && !statusData && (
        <div className="text-sm text-[#9AA5B4] py-6 text-center">
          {t("saved_tours.extension.checking_status")}
        </div>
      )}

      {statusData && (
        <>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span
                className={`w-2 h-2 rounded-full ${connected ? "bg-[#22C55E]" : "bg-[#D1D5DB]"}`}
              />
              <span className="text-sm font-medium text-[#1F2937]">
                {connected
                  ? t("saved_tours.extension.connected")
                  : t("saved_tours.extension.not_connected")}
              </span>
            </div>
            {connected && (
              <span className="text-xs text-[#9AA5B4]">
                {tokens.length}/{maxTokens}
              </span>
            )}
          </div>

          {!connected && !connectionString && (
            <p className="text-sm text-[#6B7280] mb-4">{t("saved_tours.extension.description")}</p>
          )}

          {tokens.length > 0 && (
            <div className="space-y-2 mb-4">
              {tokens.map((token) => (
                <div
                  key={token.id}
                  className="flex items-center justify-between gap-2 bg-[#F4F6F9] border border-[#E7EAEF]
                             rounded-xl p-3"
                >
                  <div className="min-w-0">
                    <div className="text-xs font-mono text-[#1F2937]">{token.prefix}…</div>
                    <div className="text-[11px] text-[#9AA5B4] mt-0.5">
                      {token.created_at && (
                        <span>
                          {t("saved_tours.extension.created_at", {
                            date: formatDate(token.created_at),
                          })}
                        </span>
                      )}
                      <span className="ml-2">
                        {t("saved_tours.extension.last_used", {
                          date: token.last_used_at
                            ? formatDate(token.last_used_at)
                            : t("saved_tours.extension.never_used"),
                        })}
                      </span>
                    </div>
                  </div>

                  {confirmRevokeId !== token.id ? (
                    <button
                      onClick={() => setConfirmRevokeId(token.id)}
                      disabled={revoking}
                      className="shrink-0 text-xs font-medium text-[#EF4444] hover:underline
                                 disabled:opacity-50"
                    >
                      {t("saved_tours.extension.revoke_access")}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleRevoke(token.id)}
                      disabled={revoking}
                      className="shrink-0 py-1.5 px-3 rounded-lg bg-[#EF4444] text-white text-xs font-medium
                                 hover:bg-[#DC2626] transition-colors disabled:opacity-50"
                    >
                      {revoking
                        ? t("saved_tours.extension.revoking")
                        : t("saved_tours.extension.confirm_revoke")}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {connectionString && (
            <div className="mb-4">
              <div className="text-xs text-[#6B7280] mb-2">
                {t("saved_tours.extension.code_once")}
              </div>
              <div className="bg-[#F4F6F9] border border-[#E7EAEF] rounded-xl p-3 mb-2">
                <code className="text-xs text-[#1F2937] break-all font-mono">
                  {connectionString}
                </code>
              </div>
              <button
                onClick={handleCopy}
                className="w-full py-2.5 rounded-xl bg-[#1F2937] text-white text-sm font-medium
                           hover:bg-[#111827] transition-colors"
              >
                {copied
                  ? t("saved_tours.extension.copied_code")
                  : t("saved_tours.extension.copy_code")}
              </button>
            </div>
          )}

          {error && <div className="text-xs text-[#EF4444] mb-3">{error}</div>}

          <div className="mt-2">
            <button
              onClick={handleGenerate}
              disabled={generating || limitReached}
              className="w-full py-2.5 rounded-xl border border-[#E7EAEF] text-sm font-medium
                         text-[#1F2937] hover:bg-[#F4F6F9] transition-colors disabled:opacity-50"
            >
              {generating
                ? t("saved_tours.extension.generating")
                : limitReached
                  ? t("saved_tours.extension.limit_reached")
                  : t("saved_tours.extension.generate_code")}
            </button>
          </div>

          {connected && (
            <p className="text-[11px] text-[#9AA5B4] mt-3">
              {t("saved_tours.extension.revoke_warning")}
            </p>
          )}
        </>
      )}
    </div>
  )
}

export default ExtensionTab
